import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useQueueAlertSettings } from '@/hooks/useQueueAlertSettings';
import { measureChimeOutput, getAudioStatus, getLastChimeAt } from '@/hooks/useQueueAlert';
import styles from './QueueAlertSettingsPanel.module.scss';

/**
 * The alert preferences, as a panel rather than a single mute button.
 *
 * A lone mute icon forces one answer to four different questions. In practice the people
 * working the queue want different combinations — the front desk wants the chime, someone on
 * a call wants it silent but still wants the count in the tab, someone who declined the
 * browser prompt wants to know why nothing pops up. So each signal is its own switch.
 *
 * Everything here is per person and per browser and never leaves the machine; the office-wide
 * settings (the email switch and address) live in the management settings screen, because
 * those SHOULD be shared.
 */

/** Chrome and Safari refuse a notification prompt outside a click; this panel is one. */
function requestNotificationPermission(): void {
  if (!('Notification' in window) || Notification.permission !== 'default') return;
  void Notification.requestPermission();
}

interface Props {
  onClose: () => void;
}

export default function QueueAlertSettingsPanel({ onClose }: Props) {
  const { t } = useTranslation();
  const { settings, update } = useQueueAlertSettings();

  /* נקרא בכל פתיחה של הפאנל: זה הרגע שבו המידע צריך להיות עדכני. */
  const audioStatus = getAudioStatus();
  const lastChime = getLastChimeAt();

  const [isMeasuring, setIsMeasuring] = useState(false);
  /** null = לא נמדד עדיין בפתיחה הזאת. */
  const [measuredPeak, setMeasuredPeak] = useState<number | null>(null);

  const notificationsBlocked = 'Notification' in window && Notification.permission === 'denied';
  const notificationsUnasked = 'Notification' in window && Notification.permission === 'default';

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className="body-sm-semibold">{t('inquiries.alerts.settingsTitle')}</span>
        <span className={`body-xs-sm-regular ${styles.scope}`}>{t('inquiries.alerts.thisComputerOnly')}</span>
      </div>

      {/* ---- הצליל ---- */}
      <label className={styles.row}>
        <input
          type="checkbox"
          checked={settings.sound}
          onChange={(e) => update({ sound: e.target.checked })}
        />
        <span className="body-sm-regular">{t('inquiries.alerts.soundOn')}</span>
      </label>

      <div className={settings.sound ? styles.volumeRow : styles.volumeRowDisabled}>
        <VolumeUpIcon sx={{ fontSize: 17 }} />
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={settings.volume}
          disabled={!settings.sound}
          onChange={(e) => update({ volume: Number(e.target.value) })}
          aria-label={t('inquiries.alerts.volume')}
          className={styles.slider}
        />
        <span className={`body-xs-sm-regular ${styles.volumeValue}`}>{settings.volume}%</span>
        {/* בלי האזנה אין דרך לדעת אם עוצמה 20 נשמעת בכלל במחשב הזה */}
        <button
          type="button"
          className={`body-xs-sm-regular ${styles.testBtn}`}
          disabled={!settings.sound || isMeasuring}
          onClick={async () => {
            setIsMeasuring(true);
            /*
             * מודד ולא רק מנגן. "לחצתי ולא שמעתי" יכול להיות שני דברים שונים
             * לגמרי - שהקוד מייצר שקט, או שהקוד מייצר קול שהמחשב לא משמיע
             * (לשונית מושתקת בכרום, מערכת מושתקת, פלט אחר). המדידה מפרידה.
             */
            const peak = await measureChimeOutput(settings.volume);
            setMeasuredPeak(peak);
            setIsMeasuring(false);
          }}
        >
          {t(isMeasuring ? 'inquiries.alerts.testing' : 'inquiries.alerts.test')}
        </button>
      </div>

      <div className={styles.divider} />

      {/* ---- התראת הדפדפן ---- */}
      <label className={styles.row}>
        <input
          type="checkbox"
          checked={settings.desktopNotification}
          onChange={(e) => {
            update({ desktopNotification: e.target.checked });
            if (e.target.checked) requestNotificationPermission();
          }}
        />
        <span className="body-sm-regular">{t('inquiries.alerts.desktopOn')}</span>
      </label>

      {settings.desktopNotification && notificationsUnasked && (
        <button type="button" className={`body-xs-sm-regular ${styles.permissionBtn}`} onClick={requestNotificationPermission}>
          {t('inquiries.alerts.grantPermission')}
        </button>
      )}

      {/*
        השורה הזאת קיימת כי בלעדיה זה נראה כמו תקלה: המתג דלוק, ושום דבר לא
        קופץ. חסימה בדפדפן אי אפשר לבטל מתוך הדף - רק מהגדרות האתר.
      */}
      {settings.desktopNotification && notificationsBlocked && (
        <span className={`body-xs-sm-regular ${styles.blockedNote}`}>
          {t('inquiries.alerts.blockedByBrowser')}
        </span>
      )}

      <div className={styles.divider} />

      {/* ---- כותרת הטאב ---- */}
      <label className={styles.row}>
        <input
          type="checkbox"
          checked={settings.tabTitle}
          onChange={(e) => update({ tabTitle: e.target.checked })}
        />
        <span className="body-sm-regular">{t('inquiries.alerts.tabTitleOn')}</span>
      </label>

      <span className={`body-xs-sm-regular ${styles.footnote}`}>
        {t('inquiries.alerts.footnote')}
      </span>

      {/*
        * מצב השמע, גלוי. "לא שמעתי צליל" יכול לנבוע משלוש סיבות שונות לגמרי -
        * המתג כבוי, הדפדפן חוסם עד הקליק הראשון, או שאין WebAudio - ומבחוץ הן
        * נראות זהות. בלי השורה הזאת אבחון דורש לפתוח devtools של מישהו אחר.
        */}
      <div className={styles.diagnostics}>
        <div className={styles.diagRow}>
          <span>{t('inquiries.alerts.diagAudio')}</span>
          <span className={audioStatus === 'ready' ? styles.diagOk : styles.diagWarn}>
            {t(`inquiries.alerts.audio.${audioStatus}`)}
          </span>
        </div>
        <div className={styles.diagRow}>
          <span>{t('inquiries.alerts.diagLastChime')}</span>
          <span>
            {lastChime
              ? lastChime.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
              : t('inquiries.alerts.never')}
          </span>
        </div>

        {/*
          * המדידה: האות שיצא בפועל מגרף השמע. אות בריא בלי שמיעה = הקול מיוצר
          * ונחסם אחרי הדף (לשונית מושתקת, מערכת מושתקת, פלט אחר). אות אפס =
          * התקלה אצלנו.
          */}
        {measuredPeak !== null && (
          <div className={styles.diagRow}>
            <span>{t('inquiries.alerts.diagMeasured')}</span>
            <span className={measuredPeak > 0.01 ? styles.diagOk : styles.diagWarn}>
              {measuredPeak > 0.01
                ? t('inquiries.alerts.signalOk', { percent: Math.round(measuredPeak * 100) })
                : t('inquiries.alerts.signalNone')}
            </span>
          </div>
        )}
      </div>

      {/* מה לעשות עם התוצאה - כדי שלא תישאר מספר בלי משמעות */}
      {measuredPeak !== null && (
        <span className={`body-xs-sm-regular ${styles.footnote}`}>
          {t(measuredPeak > 0.01 ? 'inquiries.alerts.signalOkHint' : 'inquiries.alerts.signalNoneHint')}
        </span>
      )}

      <button type="button" className={`body-sm-semibold ${styles.doneBtn}`} onClick={onClose}>
        {t('inquiries.alerts.done')}
      </button>
    </div>
  );
}
